<template>
    <div class="role-v3-container">
        <v-app class="ppv2-bg-white dark:ppv2-bg-dark ppv2-rounded-3xl ppv2-p-6">
            <!-- Header -->

            <div class="d-flex flex-row align-items-center" :style="{ marginTop: '10px', marginBottom: normalizeSize(20, true) }">
                <h1
                    class="ppv2-text-black poppinsRegular uppercaseFirstLetter mb-0 title-header"
                    :style="{ fontSize: normalizeFontSize(26) }"
                >
                    {{ isEdit ? $t('roles.edit') : $t('roles.add') }}
                </h1>
            </div>

            <div class="ppv2-w-full">
                <div class="ppv2-flex ppv2-flex-col md:ppv2-flex-row" :style="{ gap: '2%' }">
                    <!-- Left Column -->
                    <div class="ppv2-w-full md:ppv2-w-[75%]">
                        <!-- Main Information -->
                        <v-card
                            class="!ppv2-rounded-[10px] ppv2-border !ppv2-border-black !ppv2-border-opacity-20 ppv2-mb-6 ppv2-shadow-none"
                        >
                            <div class="ppv2-flex ppv2-items-center ppv2-justify-between ppv2-p-6">
                                <h2 class="poppinsSemiBold ppv2-text-black mb-0" :style="{ fontSize: normalizeFontSize(18) }">
                                    {{ $t('roles.general_info') }}
                                </h2>
                            </div>
                            <v-divider class="ppv2-m-0"></v-divider>

                            <div class="ppv2-flex ppv2-flex-wrap ppv2-p-6">
                                <div class="ppv2-w-full md:ppv2-w-1/2 ppv2-px-3">
                                    <CustomInput
                                        :label="$t('roles.display_name')"
                                        v-model="form.display_name"
                                        :placeholder="$t('roles.placeholder_display_name')"
                                        :required="true"
                                        :errorText="errors.display_name"
                                        :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                                        customLabelClass="poppinsLight ppv2-text-black"
                                    />
                                </div>
                                <div class="ppv2-w-full md:ppv2-w-1/2 ppv2-px-3">
                                    <CustomInput
                                        :label="$t('roles.unique_name')"
                                        v-model="form.name"
                                        :placeholder="$t('roles.placeholder_unique_name')"
                                        :required="true"
                                        :disabled="isEdit"
                                        :errorText="errors.name"
                                        :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                                        customLabelClass="poppinsLight ppv2-text-black"
                                    />
                                </div>
                            </div>
                        </v-card>

                        <!-- Permissions -->
                        <v-card
                            class="!ppv2-rounded-[10px] ppv2-border !ppv2-border-black !ppv2-border-opacity-20 ppv2-mb-6 ppv2-shadow-none"
                        >
                            <div class="ppv2-flex ppv2-items-center ppv2-justify-between ppv2-p-6">
                                <h2 class="poppinsSemiBold ppv2-text-black mb-0" :style="{ fontSize: normalizeFontSize(18) }">
                                    {{ $t('roles.permissions') }}
                                </h2>
                                <div class="ppv2-flex ppv2-gap-2">
                                    <BaseButton
                                        variant="secondary"
                                        @click="selectAllPermissions"
                                        :customFontSize="12"
                                        :customHeight="32"
                                        >{{ $t('roles.select_all') }}</BaseButton
                                    >
                                    <BaseButton
                                        variant="secondary"
                                        @click="deselectAllPermissions"
                                        :customFontSize="12"
                                        :customHeight="32"
                                        >{{ $t('roles.deselect_all') }}</BaseButton
                                    >
                                </div>
                            </div>
                            <v-divider class="ppv2-m-0"></v-divider>

                            <div class="ppv2-p-6">
                                <div v-if="loadingData" class="ppv2-text-center ppv2-py-4">
                                    <v-progress-circular indeterminate color="primary"></v-progress-circular>
                                </div>
                                <div
                                    v-else
                                    class="ppv2-grid ppv2-grid-cols-1 md:ppv2-grid-cols-2 lg:ppv2-grid-cols-3 ppv2-mx-3"
                                    :style="{ gap: normalizeSize(16, true) }"
                                >
                                    <div
                                        v-for="permission in permissions"
                                        :key="permission.id"
                                        class="ppv2-flex ppv2-items-center ppv2-gap-2"
                                    >
                                        <BaseSwitch
                                            v-model="form.permissions"
                                            :value="permission.name"
                                            :label="permission.name"
                                            :disabled="
                                                !isSuperAdmin &&
                                                !form.privilegedPermissions.includes(permission.name) &&
                                                planLevel <
                                                    (permissionLevels && permissionLevels[permission.name] !== undefined
                                                        ? permissionLevels[permission.name]
                                                        : 0)
                                            "
                                            :customLabelStyle="`font-size: ${normalizeSize(13, true)}`"
                                            customLabelClass="poppinsLight ppv2-text-black"
                                        />
                                        <!-- Checkbox privilège (super-admin only) -->
                                        <label
                                            v-if="
                                                isSuperAdmin &&
                                                permissionLevels &&
                                                permissionLevels[permission.name] !== undefined
                                            "
                                            class="ppv2-flex ppv2-items-center ppv2-gap-1 ppv2-cursor-pointer"
                                            :title="$t('roles.privileged_access_tooltip')"
                                        >
                                            <input
                                                type="checkbox"
                                                :value="permission.name"
                                                v-model="form.privilegedPermissions"
                                                class="ppv2-w-4 ppv2-h-4 ppv2-accent-green-600"
                                            />
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="14"
                                                height="14"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                :stroke="
                                                    form.privilegedPermissions.includes(permission.name) ? '#16a34a' : '#9ca3af'
                                                "
                                                stroke-width="2"
                                                stroke-linecap="round"
                                                stroke-linejoin="round"
                                            >
                                                <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
                                                <path d="M7 11V7a5 5 0 0 1 9.9-1" />
                                            </svg>
                                        </label>
                                        <span
                                            v-if="
                                                permissionLevels &&
                                                permissionLevels[permission.name] !== undefined &&
                                                permissionLevels[permission.name] > 0
                                            "
                                            class="badge-primary ppv2-text-xs ppv2-gap-1"
                                            :title="
                                                planLevel >= permissionLevels[permission.name]
                                                    ? $t('roles.accessible_with_plan')
                                                    : $t('roles.higher_plan_required')
                                            "
                                        >
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="12"
                                                height="12"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                stroke-width="2"
                                                stroke-linecap="round"
                                                stroke-linejoin="round"
                                            >
                                                <polygon
                                                    points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"
                                                />
                                            </svg>
                                            {{
                                                planNames[permissionLevels[permission.name]] ||
                                                $t('common.plan') + ' ' + permissionLevels[permission.name]
                                            }}
                                        </span>
                                        <!-- Cadenas ouvert : permission donnée par super-admin malgré plan insuffisant (non super-admin view) -->
                                        <span
                                            v-if="
                                                !isSuperAdmin &&
                                                form.privilegedPermissions.includes(permission.name) &&
                                                planLevel < (permissionLevels[permission.name] || 0)
                                            "
                                            class="ppv2-text-green-600"
                                            :title="$t('roles.access_granted_by_admin')"
                                        >
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="16"
                                                height="16"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                stroke-width="2"
                                                stroke-linecap="round"
                                                stroke-linejoin="round"
                                            >
                                                <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
                                                <path d="M7 11V7a5 5 0 0 1 9.9-1" />
                                            </svg>
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </v-card>
                    </div>

                    <!-- Right Column -->
                    <div class="ppv2-w-full md:ppv2-w-[25%]">
                        <!-- Users assignment -->
                        <v-card
                            class="!ppv2-rounded-[10px] ppv2-border !ppv2-border-black !ppv2-border-opacity-20 ppv2-mb-6 ppv2-shadow-none"
                        >
                            <div class="ppv2-flex ppv2-items-center ppv2-justify-between ppv2-p-6">
                                <h2 class="poppinsSemiBold ppv2-text-black mb-0" :style="{ fontSize: normalizeFontSize(18) }">
                                    {{ $t('roles.user_assignment') }}
                                </h2>
                            </div>
                            <v-divider class="ppv2-m-0"></v-divider>

                            <div class="ppv2-p-6">
                                <div v-if="loadingData" class="ppv2-text-center ppv2-py-4">
                                    <v-progress-circular indeterminate color="primary"></v-progress-circular>
                                </div>
                                <div v-else class="ppv2-space-y-3">
                                    <div
                                        v-for="user in assignedUsers"
                                        :key="user.id"
                                        class="ppv2-flex ppv2-items-center ppv2-gap-2"
                                    >
                                        <p class="poppinsLight ppv2-text-black mb-0" :style="{ fontSize: normalizeFontSize(14) }">
                                            {{ user.first_name }} {{ user.last_name }}
                                        </p>
                                    </div>
                                    <p
                                        v-if="assignedUsers.length === 0 && !loadingData"
                                        class="ppv2-text-gray-500 poppinsLight ppv2-text-sm"
                                    >
                                        {{ $t('roles.no_users') }}
                                    </p>
                                </div>
                            </div>
                        </v-card>
                    </div>
                </div>
                <div class="ppv2-flex ppv2-flex-col md:ppv2-flex-row" :style="{ gap: '2%' }">
                    <!-- Bottom Actions -->
                    <div class="ppv2-w-full md:ppv2-w-[63%]">
                        <div
                            class="ppv2-flex ppv2-justify-between ppv2-items-center"
                            :style="{ marginTop: normalizeSize(24), marginBottom: normalizeSize(40, true) }"
                        >
                            <BackButton type="button" @click="cancel" />
                            <BaseButton
                                variant="primary"
                                :loading="isSaving"
                                @click="save"
                                :customHeight="48"
                                customClass="!ppv2-min-w-[160px] !ppv2-bg-[#7D2DBD] hover:!ppv2-bg-[#6b26a3]"
                            >
                                {{ $t('common.validate') }}
                            </BaseButton>
                        </div>
                    </div>
                </div>
            </div>
        </v-app>

        <!-- Exit Dialog -->
        <CustomDialog v-model="showExitPopup" :defaultTitle="$t('experience.titleClosePopup')" heightClass="ppv2-max-h-[40vh]">
            <template #content>
                <p class="ppv2-text-center ppv2-text-black poppinsRegular" :style="{ fontSize: normalizeFontSize(15) }">
                    {{ $t('experience.descriptionClosePopup') }}
                </p>
            </template>
            <template #actions>
                <BaseButton variant="outline" @click="exit">{{ $t('experience.btnLeave') }}</BaseButton>
                <BaseButton variant="primary" @click="showExitPopup = false">{{ $t('experience.btnStay') }} </BaseButton>
            </template>
        </CustomDialog>
    </div>
</template>

<script>
import CustomInput from '../../Inputs/CustomInput.vue'
import BackButton from '../../Shared/BackButton.vue'
import BaseButton from '../../Shared/BaseButton.vue'
import BaseSwitch from '../../Shared/BaseSwitch.vue'
import CustomDialog from '../../Shared/CustomDialog.vue'
import normalizeSize from '../../../utils/normalizeSize'
import axios from 'axios'
import normalizeFontSize from '../../../utils/normalizeFontSize'

export default {
    name: 'RoleForm',
    components: {
        CustomInput,
        BackButton,
        BaseButton,
        BaseSwitch,
        CustomDialog,
    },
    props: {
        token: { type: String, required: true },
        roleId: { type: Number, default: null },
        planLevel: { type: Number, required: true },
        permissionLevels: { type: Object, required: true },
        permissionsGivenBySuperAdmin: { type: Array, required: true },
        planNames: { type: Object, required: true },
        isSuperAdmin: { type: Boolean, required: true },
    },
    data() {
        return {
            isSaving: false,
            loadingData: false,
            showExitPopup: false,
            form: {
                id: null,
                name: '',
                display_name: '',
                permissions: [],
                privilegedPermissions: [],
                initialPrivilegedPermissions: [],
                users: [],
            },
            permissions: [],
            users: [],
            errors: {},
            initialForm: null,
        }
    },
    computed: {
        isEdit() {
            return !!this.roleId
        },
        assignedUsers() {
            if (!this.form.users || this.form.users.length === 0) return []
            return this.users.filter((user) => this.form.users.includes(user.id))
        },
    },
    async created() {
        this.loadingData = true
        try {
            await Promise.all([this.fetchPermissions(), this.fetchUsers()])

            // Initialiser les permissions privilégiées depuis les props (état actuel en DB)
            this.form.privilegedPermissions = [...this.permissionsGivenBySuperAdmin]
            this.form.initialPrivilegedPermissions = [...this.permissionsGivenBySuperAdmin]

            if (this.isEdit) {
                await this.fetchRole()
            }
        } finally {
            this.loadingData = false
            this.initialForm = JSON.stringify(this.form)
        }
    },
    methods: {
        normalizeFontSize,
        normalizeSize,
        async fetchPermissions() {
            try {
                const response = await axios.get('/api/v3/roles/data/permissions', {
                    headers: { Authorization: `Bearer ${this.token}` },
                })
                this.permissions = response.data.data
            } catch (error) {
                this.$toast.error(error.response.data.message)
            }
        },
        async fetchUsers() {
            try {
                const response = await axios.get('/api/v3/roles/data/users', {
                    headers: { Authorization: `Bearer ${this.token}` },
                })
                this.users = response.data.data
            } catch (error) {
                this.$toast.error(error.response.data.message)
                console.error('Error fetching users:', error)
            }
        },
        async fetchRole() {
            try {
                const response = await axios.get(`/api/v3/roles/${this.roleId}`, {
                    headers: { Authorization: `Bearer ${this.token}` },
                })
                const role = response.data.data
                this.form = {
                    id: role.id,
                    name: role.name,
                    display_name: role.display_name,
                    permissions: role.permissions.map((p) => p.name),
                    privilegedPermissions: [...this.permissionsGivenBySuperAdmin],
                    initialPrivilegedPermissions: [...this.permissionsGivenBySuperAdmin],
                    users: role.users_ids,
                }
            } catch (error) {
                this.$toast.error(error.response.data.message)
                console.error('Error fetching role:', error)
                this.$toast.error(this.$t('roles.errors.fetch_role'))
            }
        },
        selectAllPermissions() {
            this.form.permissions = this.permissions.map((p) => p.name)
        },
        deselectAllPermissions() {
            this.form.permissions = []
        },
        cancel() {
            if (JSON.stringify(this.form) !== this.initialForm) {
                this.showExitPopup = true
            } else {
                this.exit()
            }
        },
        exit() {
            window.location.href = '/back-office/v3/roles'
        },
        validateForm() {
            this.errors = {}
            let isValid = true

            if (!this.form.display_name) {
                this.errors.display_name = this.$t('roles.errors.display_name_required')
                isValid = false
            }
            if (!this.isEdit && !this.form.name) {
                this.errors.name = this.$t('roles.errors.unique_name_required')
                isValid = false
            }

            return isValid
        },
        async save() {
            if (!this.validateForm()) {
                this.$toast.error(this.$t('roles.errors.form_validation'))
                return
            }

            this.isSaving = true
            try {
                const url = this.isEdit ? `/api/v3/roles/${this.roleId}` : '/api/v3/roles/store'
                const method = this.isEdit ? 'put' : 'post'

                await axios({
                    method,
                    url,
                    data: this.form,
                    headers: { Authorization: `Bearer ${this.token}` },
                })

                this.$toast.success(
                    this.isEdit ? this.$t('roles.messages.update_success') : this.$t('roles.messages.create_success')
                )
                this.initialForm = JSON.stringify(this.form)
                this.exit()
            } catch (error) {
                this.$toast.error(error.response.data.message)
                console.error('Error saving role:', error)
                if (error.response && error.response.data && error.response.data.errors) {
                    this.errors = error.response.data.errors
                }
                this.$toast.error(this.$t('roles.errors.save_error'))
            } finally {
                this.isSaving = false
            }
        },
    },
}
</script>

<style scoped lang="scss">
.role-v3-container {
    padding: 0;
}

.title-header {
    letter-spacing: -0.02em;
}

:deep(.v-application--wrap) {
    min-height: auto;
}

:deep(.v-card) {
    border-radius: 10px !important;
}
</style>
